import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken } from '../../../slices/staffSlice';
import { Topbar, Sidebar, Loader, Member, Pagination, Search } from "../../../components";
import { adminPortalRequest } from "../../../Requests/getRequests";
import { authStaff } from '../../../utils/constants';
import { show } from '../../../slices/toastSlice';
import AddStaff from './AddStaff';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { UserIcon, FileAddIcon } from '@hugeicons/core-free-icons';

function Index() {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [staffList, setStaffList] = useState();
    const [defaultData, setDefaultData] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [metadata, setMetadata] = useState(null);
    const [currentPage, setCurrentPage] = useState(metadata?.currentPage || 1);
    const nPages = metadata?.totalPages;
    const [isOpen, setIsOpen] = useState(false);

    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);

    const handlePagination = async(page) => {
        setIsLoading(true);
        const res = await adminPortalRequest(`/auth/staff/list`, "GET", authStaff(accessToken), { page: page || 1 }, {});
        setIsLoading(false);

        if(res?.code === 401) {
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            return;
        }
        if(res?.err) { dispatch(show({ type: "error", state: true, message: res?.err })); return; }

        setStaffList(res.staff);
        setDefaultData(res.staff);
        setMetadata(res.metadata);
    };

    useEffect(() => {
        let unsubscribed = false;
        const load = async() => {
            setIsLoading(true);
            const res = await adminPortalRequest(`/auth/staff/list`, "GET", authStaff(accessToken), { page: 1 }, {});
            setIsLoading(false);
            if(unsubscribed) return;
            if(res?.code === 401) {
                sessionStorage.clear();
                navigate("/login");
                dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
                return;
            }
            setStaffList(res.staff);
            setDefaultData(res.staff);
            setMetadata(res.metadata);
        };
        load();
        return () => { unsubscribed = true; };
    }, [accessToken, dispatch, navigate]);

    return (
        <>
        <div className="main-container">
            <Topbar />
            <Sidebar />

            <main className="main-content">
                <section className="section-container space-y-6">

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                                <HugeiconsIcon icon={UserIcon} size={18} className="text-[#1e3a6e]" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Staff</h1>
                                    {metadata?.totalCount > 0 && (
                                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                                            {metadata.totalCount}
                                        </span>
                                    )}
                                    {isLoading && <Loader color="text-gray-400" size="w-4 h-4" />}
                                </div>
                                <p className="text-sm text-gray-400">Manage staff accounts and roles</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Search
                                data={staffList}
                                setData={setStaffList}
                                placeholder="Search staff"
                                defaultData={defaultData}
                            />
                            <button
                                type="button"
                                onClick={openModal}
                                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] rounded-lg hover:bg-[#264d91] transition-colors shrink-0"
                            >
                                <HugeiconsIcon icon={FileAddIcon} size={15} />
                                Add Staff
                            </button>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-gray-50/60 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-700">
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Name</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email Address</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone Number</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                {staffList && staffList.length > 0 ? (
                                    staffList.map((item, index) => (
                                        <Member key={index} item={item} />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-5 py-10 text-center text-sm text-gray-400">
                                            {isLoading ? 'Loading...' : 'No staff members found'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {staffList && (
                            <Pagination
                                nPages={nPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                length={metadata.totalCount}
                                handlePagination={handlePagination}
                            />
                        )}
                    </div>

                </section>
            </main>
        </div>

        <AddStaff isOpen={isOpen} closeModal={closeModal} />
        </>
    );
}

export default Index;
